import { action } from 'mobx';
import { uniqBy } from 'lodash';
import RunbookModel from 'app/views/core/runbooks/RunbookModel';
import { Collection } from 'core/model';

class RunbookRevisionCollection extends Collection {
  runbookId;

  constructor(data = [], options = {}) {
    super(data, options);
    this.runbookId = options.runbookId;
  }

  /**
   * Override add to deduplicate by revision_id instead of model.id.
   * All revisions share the same runbook id but differ by revision_id,
   * so the default uniqBy(model.id) would collapse them into one.
   */
  @action
  add = (data) => {
    if (!Array.isArray(data)) {
      data = [data];
    }

    const models = data.map((row) => {
      if (row instanceof RunbookModel) {
        row.collection = this;
        return row;
      }
      return this.build(row);
    });

    this.original = uniqBy((this.original || []).concat(models), (model) => model.revisionId);
    this.filter();
    this.hashModelsById();
    this.setLastUpdated();

    return models;
  };

  get url() {
    if (!this.runbookId) {
      throw new Error('RunbookRevisionCollection requires runbookId');
    }
    return `/api/ui/runbook/${this.runbookId}/revisions`;
  }

  get model() {
    return RunbookModel;
  }

  get defaultSortState() {
    return {
      field: 'revisionId',
      direction: 'desc'
    };
  }

  hashModelsById = () => {
    const modelHash = {};
    this.get().forEach((model) => {
      if (model) {
        const { revisionId } = model;
        if (revisionId) {
          modelHash[revisionId] = model;
        }
        // also keep the original id for backwards compatibility
        if (model.id) {
          modelHash[model.id] = model;
        }
      }
    });
    this.modelById = modelHash;
  };

  // override get to handle revision_id lookups
  get(key) {
    if (key !== undefined) {
      // first try direct lookup in our hash
      const model = this.modelById[key];
      if (model) {
        return model;
      }

      // fallback to searching by revision_id
      return this.models.find((m) => m.revisionId === key);
    }

    // return all models (default behavior)
    const models = this.original || this.models;
    return models.slice();
  }

  // get the previous revision for comparison
  getPreviousRevision(currentRevision) {
    const currentIndex = this.models.indexOf(currentRevision);
    return currentIndex >= 0 && currentIndex < this.models.length - 1 ? this.models[currentIndex + 1] : null;
  }
}

export default RunbookRevisionCollection;
